<template>
  <div class="ibb-sheet">
    <div class="ibb-sheet__topbar">
      <button class="ibb-sheet__back" @click="goBack" :title="$t('buttons.close') || 'Cerrar'">
        ←
      </button>
      <span class="ibb-sheet__filename">{{ filename }}</span>
      <span class="ibb-sheet__status" :class="statusClass">
        <span class="ibb-sheet__status-dot" v-if="statusIconKind"></span>
        {{ statusText }}
      </span>
      <div class="ibb-sheet__export-wrap" v-click-outside="closeExportMenu">
        <button
          class="ibb-sheet__history"
          @click="toggleExportMenu"
          :title="$t('files.exportMenu') || 'Exportar e imprimir'"
        >
          <span class="ibb-sheet__history-icon">⬇</span>
          <span class="ibb-sheet__history-text">{{ $t('files.exportMenu') || 'Exportar' }}</span>
        </button>
        <ul v-if="showExportMenu" class="ibb-sheet__export-menu">
          <li @click="exportXlsx">{{ $t('files.downloadXlsx') || 'Descargar .xlsx' }}</li>
          <li @click="exportCsv">{{ $t('files.downloadCsv') || 'Descargar .csv' }}</li>
          <li @click="printSheet">{{ $t('files.print') || 'Imprimir' }}</li>
        </ul>
      </div>
      <button
        v-if="!isReadOnly"
        class="ibb-sheet__history"
        @click="showHistory = true"
        :title="$t('files.versionHistory') || 'Historial de versiones'"
      >
        <span class="ibb-sheet__history-icon">⟲</span>
        <span class="ibb-sheet__history-text">{{ $t('files.versionHistory') || 'Historial' }}</span>
      </button>
      <button
        v-if="!isReadOnly"
        class="ibb-sheet__save"
        :disabled="saving || !dirty"
        @click="save"
      >
        {{ $t('buttons.save') || 'Guardar' }}
      </button>
    </div>

    <div ref="mount" class="ibb-sheet__mount"></div>

    <div v-if="!loaded" class="ibb-sheet__loading">
      <div class="ibb-sheet__loading-box">
        <h2>{{ $t('files.loading') || 'Cargando…' }}</h2>
        <p v-if="loadPhase">{{ loadPhase }}</p>
      </div>
    </div>

    <version-history
      v-if="showHistory"
      :node-handle="req && req.handle"
      :parent-handle="req && req.parentHandle"
      :filename="filename"
      :ibb-instance="ibbInstanceForFile"
      @close="showHistory = false"
      @restored="onVersionRestored"
    />

    <!-- Modal de confirmaci\u00f3n para cambios sin guardar al pulsar \u2190 -->
    <div v-if="showUnsavedModal" class="ibb-sheet-modal" @click.self="cancelUnsavedModal">
      <div class="ibb-sheet-modal__card">
        <div class="ibb-sheet-modal__header">
          <h2>{{ $t('prompts.unsavedChangesTitle') || 'Cambios sin guardar' }}</h2>
        </div>
        <div class="ibb-sheet-modal__body">
          {{ $t('prompts.unsavedChanges') || 'Hay cambios sin guardar. \u00bfQuieres salir igualmente?' }}
        </div>
        <div class="ibb-sheet-modal__footer">
          <button class="ibb-sheet-modal__btn" @click="cancelUnsavedModal">
            {{ $t('buttons.cancel') || 'Cancelar' }}
          </button>
          <button class="ibb-sheet-modal__btn ibb-sheet-modal__btn--save" @click="saveAndLeave">
            {{ $t('prompts.saveAndLeave') || 'Guardar y salir' }}
          </button>
          <button class="ibb-sheet-modal__btn ibb-sheet-modal__btn--discard" @click="discardAndLeave">
            {{ $t('prompts.discardAndLeave') || 'Salir sin guardar' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/*
 * Editor de hojas de cálculo basado en Univer (UMD 0.4.2).
 *
 * Estrategia: overlay fullscreen (position: fixed, inset: 0) para evitar
 * pelear con el layout de Cloud.vue. Univer necesita un contenedor con
 * dimensiones concretas desde el minuto 0; un fullscreen lo garantiza.
 *
 * Flujo:
 *   mounted() → $nextTick → loadUniver() → loadFile() → listo
 *
 * Guardado:
 *   Ctrl+S / botón / autosave 30s (si dirty)
 *
 * Volver:
 *   Botón "←" hace router.back(). Desde Cloud.vue se volverá al listado.
 */

import { mapState } from 'vuex';
import { files as api } from '@/api';
import { ROUTES } from '@/router';
import {
  xlsxBytesToUniverWorkbookData,
  univerWorkbookDataToXlsxBytes,
  createEmptyUniverWorkbookData,
  xlsxBytesToCsv,
} from '@/utils/univerAdapter';
import { saveVersionSnapshot } from '@/utils/versionHistory';
import univerEsES from '@/utils/univerEsES';
import { wireUniverPanelClose } from '@/utils/univerPanelClose';
import { exportUnitToPdf } from '@/utils/univerPdfExport';
import VersionHistory from '@/components/prompts/VersionHistoryXlsx.vue';

// Self-host: los assets UMD están en /public/univer/ del frontend.
// Para volver a unpkg (debug), cambiar a 'https://unpkg.com/@univerjs/umd@0.4.2/lib'.
const UNIVER_BASE = '/univer';
const AUTOSAVE_MS = 15000; // intento de autoguardado cada 15s si hay cambios
const VERSION_SNAPSHOT_MS = 120000; // crea snapshot de versión cada 2 minutos mínimo

let univerLoaderPromise = null;

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if ([...document.scripts].some(s => s.src === src)) return resolve();
    const s = document.createElement('script');
    s.src = src;
    s.async = false;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('No se pudo cargar ' + src));
    document.head.appendChild(s);
  });
}

function loadCss(href) {
  return new Promise((resolve, reject) => {
    if ([...document.styleSheets].some(s => s.href === href)) return resolve();
    const l = document.createElement('link');
    l.rel = 'stylesheet';
    l.href = href;
    l.onload = () => resolve();
    l.onerror = () => reject(new Error('No se pudo cargar ' + href));
    document.head.appendChild(l);
  });
}

function loadUniverUMD() {
  if (univerLoaderPromise) return univerLoaderPromise;
  univerLoaderPromise = (async () => {
    await loadCss(UNIVER_BASE + '/univer.css');
    await loadScript(UNIVER_BASE + '/univer.full.umd.js');
    // Locale: 0.4.2 no publica es-ES. Cargamos en-US como base (opcional);
    // los textos de Iberbox en torno a Univer se traducen por vue-i18n.
    try { await loadScript(UNIVER_BASE + '/locale/en-US.js'); } catch (_) { /* opcional */ }
  })();
  return univerLoaderPromise;
}

export default {
  name: 'sheet-editor',
  components: { VersionHistory },
  // Directiva inline mínima: cierra el menú al clicar fuera.
  directives: {
    'click-outside': {
      bind (el, binding) {
        el.__clickOutsideHandler__ = (evt) => {
          if (!(el === evt.target || el.contains(evt.target))) {
            binding.value(evt);
          }
        };
        document.addEventListener('mousedown', el.__clickOutsideHandler__);
      },
      unbind (el) {
        document.removeEventListener('mousedown', el.__clickOutsideHandler__);
        delete el.__clickOutsideHandler__;
      }
    }
  },
  data () {
    return {
      loaded: false,
      saving: false,
      dirty: false,
      isReadOnly: false,
      loadPhase: '',
      univer: null,
      univerAPI: null,
      unitId: null,
      autosaveTimer: null,
      ignoreMutations: true,
      univerInstanceType: null,
      lastSavedAt: null,   // Date del último save exitoso
      lastVersionAt: null, // Date del último snapshot de versión guardado
      showHistory: false,
      showExportMenu: false,
      showUnsavedModal: false,
      panelCloseCleanup: null, // handler devuelto por wireUniverPanelClose
    };
  },
  computed: {
    ...mapState(['req', 'permissions', 'permissionsSharedDrive']),
    filename () { return (this.req && this.req.name) || ''; },
    ibbInstanceForFile () {
      return this.$route.name === ROUTES.DRIVE ? (typeof ibbLink !== 'undefined' ? ibbLink : null) : (typeof ibb !== 'undefined' ? ibb : null);
    },
    statusText () {
      if (this.saving) return this.$t('files.saving') || 'Guardando…';
      if (this.dirty) return this.$t('files.unsaved') || 'Cambios sin guardar';
      if (this.lastSavedAt) {
        const hhmm = this.lastSavedAt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
        return (this.$t('files.savedAt') || 'Guardado a las {time}').replace('{time}', hhmm);
      }
      if (this.loaded) return this.$t('files.saved') || 'Guardado';
      return '';
    },
    statusClass () {
      if (this.saving) return 'ibb-sheet__status--saving';
      if (this.dirty) return 'ibb-sheet__status--dirty';
      if (this.loaded) return 'ibb-sheet__status--ok';
      return '';
    },
    statusIconKind () {
      return this.saving || this.dirty || this.loaded;
    },
  },
  created () {
    window.addEventListener('keydown', this.keyEvent);
    window.addEventListener('beforeunload', this.beforeUnload);
  },
  async mounted () {
    this.isReadOnly = (this.permissions === 1 || this.permissionsSharedDrive === 1);
    try {
      this.loadPhase = 'Cargando motor…';
      await this.$nextTick();
      await loadUniverUMD();

      this.loadPhase = 'Inicializando editor…';
      this.bootUniver();

      this.loadPhase = 'Abriendo archivo…';
      await this.openUnit();

      // Espera inicial y luego re-activa detección de cambios reales
      await this.$nextTick();
      window.dispatchEvent(new Event('resize'));
      setTimeout(() => window.dispatchEvent(new Event('resize')), 120);
      setTimeout(() => { this.ignoreMutations = false; }, 1500);

      // Habilitar cerrado de paneles laterales (X + click fuera)
      this.panelCloseCleanup = wireUniverPanelClose(this.$refs.mount);

      this.loaded = true;
    } catch (e) {
      this.$showError(e);
      // eslint-disable-next-line no-console
      console.error('[SheetEditor] fallo:', e);
    }
  },
  beforeDestroy () {
    window.removeEventListener('keydown', this.keyEvent);
    window.removeEventListener('beforeunload', this.beforeUnload);
    clearTimeout(this.autosaveTimer);
    if (this.panelCloseCleanup) { try { this.panelCloseCleanup(); } catch (_) { /* noop */ } }
    if (this.univer) {
      try { this.univer.dispose(); } catch (_) { /* noop */ }
      this.univer = null;
      this.univerAPI = null;
    }
  },
  methods: {
    beforeUnload (e) {
      if (this.dirty) { e.preventDefault(); e.returnValue = ''; return ''; }
    },
    keyEvent (event) {
      if (!event.ctrlKey && !event.metaKey) return;
      if (String.fromCharCode(event.which).toLowerCase() !== 's') return;
      event.preventDefault();
      if (!this.isReadOnly) this.save();
    },
    goBack () {
      if (this.dirty) {
        // Usamos un modal propio en vez de window.confirm (feo + no estilable).
        this.showUnsavedModal = true;
        return;
      }
      this.doLeave();
    },
    doLeave () {
      this.showUnsavedModal = false;
      if (window.history.length > 1) this.$router.back();
      else this.$router.push({ path: '/cloud' });
    },
    cancelUnsavedModal () {
      this.showUnsavedModal = false;
    },
    async saveAndLeave () {
      this.showUnsavedModal = false;
      try {
        await this.save();
      } catch (_) { /* save ya reporta el error */ }
      // Solo salimos si el save ha limpiado `dirty`.
      if (!this.dirty) this.doLeave();
    },
    discardAndLeave () {
      this.dirty = false; // evita que beforeDestroy o beforeUnload vuelvan a preguntar
      this.doLeave();
    },

    bootUniver () {
      const W = window;
      const Core = W.UniverCore;
      const Design = W.UniverDesign;
      const Facade = W.UniverFacade;
      if (!Core || !Core.Univer || !Design || !Facade) {
        throw new Error('Univer UMD no expuso UniverCore/UniverDesign/UniverFacade.');
      }

      const { Univer, LocaleType, UniverInstanceType, mergeLocales } = Core;
      const { defaultTheme } = Design;
      const { FUniver } = Facade;

      // Univer 0.4.2 no publica es-ES, así que:
      //   1. Tomamos el diccionario completo en-US cargado vía UMD (`UniverUMD["en-US"]`)
      //   2. Lo mezclamos con nuestro partial es-ES (las cadenas visibles)
      //   3. Lo pasamos como el locale EN_US activo. Como Univer no conoce otras
      //      variantes de español, usar EN_US con el diccionario en español es
      //      la forma más fiable de traducir sin romper claves que no cubrimos.
      //
      // CRÍTICO: el merge DEBE ser profundo (deep). Un shallow merge (Object.assign)
      // reemplaza ramas enteras (p. ej. "toolbar") haciendo que Univer muestre
      // literalmente las claves ("sheets.tabs.sheet1") para las entradas que nuestro
      // partial no cubre dentro de esa rama.
      const deepMerge = (target, source) => {
        const out = Array.isArray(target) ? target.slice() : Object.assign({}, target);
        if (!source || typeof source !== 'object') return out;
        for (const k of Object.keys(source)) {
          const sv = source[k];
          const tv = out[k];
          if (sv && typeof sv === 'object' && !Array.isArray(sv)
              && tv && typeof tv === 'object' && !Array.isArray(tv)) {
            out[k] = deepMerge(tv, sv);
          } else {
            out[k] = sv;
          }
        }
        return out;
      };
      const enFull = (W.UniverUMD && W.UniverUMD['en-US']) || {};
      const mergedEs = mergeLocales
        ? mergeLocales(enFull, univerEsES)
        : deepMerge(enFull, univerEsES);
      const localeType = LocaleType.EN_US;
      const locales = { [LocaleType.EN_US]: mergedEs };

      const univer = new Univer({ theme: defaultTheme, locale: localeType, locales });

      const reg = (mod, pluginName, config) => {
        if (mod && mod[pluginName]) {
          config === undefined
            ? univer.registerPlugin(mod[pluginName])
            : univer.registerPlugin(mod[pluginName], config);
        }
      };

      reg(W.UniverEngineRender,    'UniverRenderEnginePlugin');
      reg(W.UniverUi,              'UniverUIPlugin', { container: this.$refs.mount });
      reg(W.UniverDocs,            'UniverDocsPlugin', { hasScroll: false });
      reg(W.UniverDocsUi,          'UniverDocsUIPlugin');
      reg(W.UniverEngineFormula,   'UniverFormulaEnginePlugin');
      reg(W.UniverSheets,          'UniverSheetsPlugin');
      reg(W.UniverSheetsUi,        'UniverSheetsUIPlugin');
      reg(W.UniverSheetsFormula,   'UniverSheetsFormulaPlugin');
      reg(W.UniverSheetsFormulaUi, 'UniverSheetsFormulaUIPlugin');
      reg(W.UniverSheetsNumfmt,    'UniverSheetsNumfmtPlugin');

      this.univer = univer;
      this.univerAPI = FUniver.newAPI(univer);
      this.univerInstanceType = UniverInstanceType;

      try {
        if (this.univerAPI.onCommandExecuted) {
          this.univerAPI.onCommandExecuted((evt) => {
            if (evt && evt.type === 2 /* MUTATION */) this.markDirty();
          });
        }
      } catch (_) { /* save manual sigue funcionando */ }
    },

    async openUnit () {
      let data;
      if (!this.req || !this.req.handle) {
        data = createEmptyUniverWorkbookData();
      } else {
        const ibbInstance = this.$route.name === ROUTES.DRIVE ? ibbLink : ibb;
        const buf = await api.getFileBytes(this.req.handle, ibbInstance);
        data = xlsxBytesToUniverWorkbookData(buf, { name: this.req.name });
      }
      const unit = this.univer.createUnit(this.univerInstanceType.UNIVER_SHEET, data);
      this.unitId = unit && (unit.getUnitId ? unit.getUnitId() : unit.unitId);
    },

    markDirty () {
      if (this.isReadOnly || this.ignoreMutations) return;
      this.dirty = true;
      clearTimeout(this.autosaveTimer);
      this.autosaveTimer = setTimeout(() => {
        if (this.dirty && !this.saving) this.save();
      }, AUTOSAVE_MS);
    },

    async save () {
      if (this.isReadOnly || this.saving) return;
      this.saving = true;
      // Bloquea los watchers de Cloud.vue durante el save: así el evento de
      // borrado del SDK (dentro de putIdBinary) no provoca un reload que
      // desmontaría temporalmente <sheet-editor> (isSheets depende de
      // !loading, y fetchData hace setLoading(true)).
      try { this.$store.commit('setEditorSaving', true); } catch (_) { /* noop */ }
      try {
        const bytes = this.getCurrentBytes();
        const ibbInstance = this.ibbInstanceForFile;

        // Callback entre upload y delete: cuando el nuevo handle ya existe,
        // actualizamos store y URL antes de que el SDK dispare el evento de
        // borrado. El watcher está bloqueado por editorSaving, pero mantener
        // la URL sincronizada evita sorpresas al reanudar.
        const self = this;
        const onNewHandle = (newHandle) => {
          if (!newHandle) return;
          try {
            if (self.req) {
              self.$store.commit('updateRequest', { ...self.req, handle: newHandle });
            }
            if (self.$route && self.$route.hash !== '#' + newHandle) {
              self.$router.replace({ path: self.$route.path, hash: '#' + newHandle });
            }
          } catch (_) { /* noop */ }
        };

        await api.putIdBinary(this.req.handle, bytes, ibbInstance, onNewHandle);

        this.dirty = false;
        this.lastSavedAt = new Date();

        // Versionado estilo 365: si ha pasado suficiente tiempo desde la
        // última versión archivada, guardamos un snapshot en la carpeta
        // oculta de versiones. Lo esperamos DENTRO del bloque saving para
        // que editorSaving permanezca activo y los listeners del SDK que
        // dispara (createFolder/createFile) no desmonten el editor.
        const now = Date.now();
        if (!this.lastVersionAt || (now - this.lastVersionAt.getTime()) > VERSION_SNAPSHOT_MS) {
          try {
            await saveVersionSnapshot({
              ibb: ibbInstance,
              parentHandle: this.req && this.req.parentHandle,
              originalName: this.req && this.req.name,
              bytes,
            });
            this.lastVersionAt = new Date();
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[SheetEditor] snapshot de versión falló (no bloqueante):', err && err.message);
          }
        }
      } catch (e) {
        this.$showError(e);
        // eslint-disable-next-line no-console
        console.error('[SheetEditor] error al guardar:', e);
      } finally {
        this.saving = false;
        // Libera el flag tras un pequeño margen para dejar drenar los
        // eventos del SDK que puedan disparar setReload justo al acabar.
        setTimeout(() => {
          try { this.$store.commit('setEditorSaving', false); } catch (_) { /* noop */ }
        }, 50);
      }
    },

    getCurrentBytes () {
      const wb = this.univerAPI && this.univerAPI.getActiveWorkbook && this.univerAPI.getActiveWorkbook();
      let snapshot = null;
      if (wb) {
        if (typeof wb.save === 'function') snapshot = wb.save();
        else if (typeof wb.getSnapshot === 'function') snapshot = wb.getSnapshot();
      }
      if (!snapshot && this.univer && typeof this.univer.getSnapshot === 'function' && this.unitId) {
        snapshot = this.univer.getSnapshot(this.unitId);
      }
      if (!snapshot) throw new Error('No se pudo obtener el estado del libro');
      return univerWorkbookDataToXlsxBytes(snapshot);
    },

    async onVersionRestored (bytes) {
      // Restaurar es una operación intrusiva: Univer 0.4.2 con `disposeUnit +
      // createUnit` deja en el DOM ambos `<section class="univer-workbench-container-canvas">`
      // superpuestos, lo que produce artefactos visuales (texto doble, negrita
      // mal renderizada, etc.). La forma fiable es:
      //   1. Guardar los bytes restaurados en el archivo vía putIdBinary.
      //   2. Destruir completamente Univer (`univer.dispose()`).
      //   3. Limpiar el mount (por si Univer deja nodos colgando).
      //   4. Reinstanciar con bootUniver + openUnit ahora leyendo el nuevo handle.
      this.showHistory = false;
      try { this.$store.commit('setEditorSaving', true); } catch (_) { /* noop */ }
      this.saving = true;
      try {
        const ibbInstance = this.ibbInstanceForFile;
        const onNewHandle = (newHandle) => {
          if (!newHandle || !this.req) return;
          try {
            this.$store.commit('updateRequest', { ...this.req, handle: newHandle });
            if (this.$route && this.$route.hash !== '#' + newHandle) {
              this.$router.replace({ path: this.$route.path, hash: '#' + newHandle });
            }
          } catch (_) { /* noop */ }
        };
        await api.putIdBinary(this.req.handle, bytes, ibbInstance, onNewHandle);

        // Versionado: la restauración cuenta como un guardado histórico más.
        try {
          await saveVersionSnapshot({
            ibb: ibbInstance,
            parentHandle: this.req && this.req.parentHandle,
            originalName: this.req && this.req.name,
            bytes,
          });
          this.lastVersionAt = new Date();
        } catch (err) { /* no bloqueante */ }

        // Reset total del motor Univer
        if (this.univer) {
          try { this.univer.dispose(); } catch (_) { /* noop */ }
          this.univer = null;
          this.univerAPI = null;
          this.unitId = null;
        }
        if (this.$refs.mount) {
          // Univer puede dejar nodos residuales tras dispose; limpieza manual.
          this.$refs.mount.innerHTML = '';
        }
        await this.$nextTick();
        this.ignoreMutations = true;
        this.bootUniver();
        await this.openUnit();
        window.dispatchEvent(new Event('resize'));
        setTimeout(() => window.dispatchEvent(new Event('resize')), 120);
        setTimeout(() => { this.ignoreMutations = false; }, 1500);

        this.dirty = false;
        this.lastSavedAt = new Date();
      } catch (e) {
        this.$showError(e);
        // eslint-disable-next-line no-console
        console.error('[SheetEditor] error al restaurar:', e);
      } finally {
        this.saving = false;
        setTimeout(() => {
          try { this.$store.commit('setEditorSaving', false); } catch (_) { /* noop */ }
        }, 50);
      }
    },

    // ---------------------- Exportar / Imprimir ----------------------
    toggleExportMenu () {
      this.showExportMenu = !this.showExportMenu;
    },
    closeExportMenu () {
      this.showExportMenu = false;
    },
    triggerDownload (bytes, mime, filename) {
      const blob = new Blob([bytes], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    },
    exportXlsx () {
      this.showExportMenu = false;
      try {
        const bytes = this.getCurrentBytes();
        const name = (this.filename && this.filename.toLowerCase().endsWith('.xlsx'))
          ? this.filename
          : (this.filename || 'hoja') + '.xlsx';
        this.triggerDownload(
          bytes,
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          name
        );
      } catch (e) {
        this.$showError(e);
      }
    },
    exportCsv () {
      this.showExportMenu = false;
      try {
        const bytes = this.getCurrentBytes();
        const csv = xlsxBytesToCsv(bytes);
        const name = (this.filename || 'hoja').replace(/\.[^./]+$/, '') + '.csv';
        // Añadimos BOM para que Excel abra el CSV con acentos correctamente.
        const withBom = '\ufeff' + csv;
        this.triggerDownload(
          new TextEncoder().encode(withBom),
          'text/csv;charset=utf-8',
          name
        );
      } catch (e) {
        this.$showError(e);
      }
    },
    /**
     * Imprimir / Exportar como PDF usando la utilidad `univerPdfExport`.
     *
     * Mismo flujo que Docs: expandir canvas, capturar a imagen, partir
     * en p\u00e1ginas A4, imprimir desde un iframe oculto. A\u00edsla el PDF
     * de la UI de Iberbox y paginat correctamente el contenido aunque
     * el viewport no lo muestre todo.
     */
    async printSheet () {
      this.showExportMenu = false;
      try {
        await exportUnitToPdf({
          mount: this.$refs.mount,
          sheet: this.$el,
          univerAPI: this.univerAPI,
          type: 'sheet',
          filename: this.filename || 'hoja.pdf',
        });
      } catch (e) {
        this.$showError ? this.$showError(e) : console.error(e);
      }
    },
  },
};
</script>

<style>
/* Los estilos .ibb-sheet* son compartidos con DocsEditor; se importan desde
 * un CSS standalone. No usamos `scoped` porque forzamos estilos sobre la DOM
 * de Univer (sus divs no llevan el atributo de scoping de Vue). */
@import "../../styles/ibb-editor.css";

.ibb-sheet__dummy {
  /* Marcador; todos los estilos activos est\u00e1n en styles/ibb-editor.css */
}

</style>
